import { Link as ReactLink } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Link,
  Button,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import * as yup from 'yup';
import { Formik, Form, Field, ErrorMessage, useField } from 'formik';
import { useAuth } from '@/hooks';
import { useLoginManager } from './login-manager';

import { VerifyUser } from './VerifyUser';
import { useEffect } from 'react';
import { Eye, EyeOff } from '@kula-ai/icons-core';

const loginSchema = yup.object({
  otpRequired: yup.boolean(),
  otp: yup.string().when('otpRequired', {
    is: true,
    then: (schema) => schema.required('OTP is required'),
  }),
  email: yup.string().email('Email is not valid').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const HiddenInput = ({ name, currValue }) => {
  const [field, meta, helpers] = useField(name);
  const { value } = meta;
  const { setValue } = helpers;
  useEffect(() => {
    if (value === currValue) return;
    setValue(currValue);
  }, [currValue, setValue, value]);
  return <Input {...field} type="hidden" />;
};

export const Login = ({ onLogin = () => {} }) => {
  const { isLoggingIn } = useAuth();
  const { otpRequired } = useLoginManager();
  const { isOpen: showPassword, onToggle: togglePassword } = useDisclosure();

  return (
    <Formik
      validateOnBlur={false}
      initialValues={{
        otpRequired,
        otp: '',
        email: '',
        password: '',
      }}
      validationSchema={loginSchema}
      onSubmit={onLogin}
    >
      <Form noValidate>
        <Field name="email">
          {({ field, form }) => (
            <FormControl
              mb="16px"
              isRequired
              isInvalid={form.errors.email && form.touched.email}
            >
              <FormLabel fontWeight="semiBold">Email address</FormLabel>
              <Input
                {...field}
                color="secondary-700"
                id="email"
                placeholder="abc@example.com"
              />
              <FormErrorMessage>
                <ErrorMessage name="email" />
              </FormErrorMessage>
            </FormControl>
          )}
        </Field>
        <HiddenInput name="otpRequired" currValue={otpRequired} />
        <Field name="password">
          {({ field, form }) => (
            <FormControl
              mb="16px"
              isRequired
              isInvalid={form.errors.password && form.touched.password}
            >
              <FormLabel fontWeight="semiBold">Password</FormLabel>
              <InputGroup>
                <Input
                  {...field}
                  value={field.value || ''}
                  color="secondary-700"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="**********"
                  pr="45px"
                />
                <InputRightElement>
                  <Icon
                    as={showPassword ? EyeOff : Eye}
                    boxSize={5}
                    color="secondary-500"
                    cursor="pointer"
                    onClick={togglePassword}
                    _hover={{ color: 'secondary-700' }}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>
                <ErrorMessage name="password" />
              </FormErrorMessage>
            </FormControl>
          )}
        </Field>

        {otpRequired && (
          <Box data-test-id="verify-user">
            <VerifyUser />
          </Box>
        )}

        <Button
          type="submit"
          id="login"
          width="full"
          isLoading={isLoggingIn}
          mb="16px"
          height={'40px'}
        >
          Login
        </Button>

        <Box textAlign="right">
          <Link
            as={ReactLink}
            to="/password_reset"
            color="primary-500"
            fontWeight="semiBold"
            fontSize="14px"
            textDecoration="underline"
            data-test-id="forgot-password-link"
          >
            Forgot password?
          </Link>
        </Box>
      </Form>
    </Formik>
  );
};
